import React from "react";

import CheckIcon from "@uui/Check";
import ChevronDownIcon from "@uui/ChevronDown";
import { filter, isEmpty } from "lodash/fp";

import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Button, { ButtonProps } from "@mui/material/Button";
import FormLabel from "@mui/material/FormLabel";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import type { UserDistributionSetting } from "@app/common/types";

import RuiAccordion, {
  AccordionSummary as RuiAccordionSummary,
  AccordionSummaryProps as RuiAccordionSummaryProps,
} from "@rui/Accordion";
import IconBackground from "@rui/IconBackground";
import Menu from "@rui/Menu";
import { Column, Row } from "@rui/Stack";
import { Img } from "@rui/styles";

import { ICON_SRC_FOR_CHANNEL } from "./constants";

export const Accordion = styled(RuiAccordion)(({ theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  borderRadius: "8px",
  "&.Mui-expanded": {
    borderColor: theme.palette.shades.primary[500],
  },
}));

type AccordionSummaryProps = RuiAccordionSummaryProps & { expandable: boolean };

export const AccordionSummary = styled<React.FC<AccordionSummaryProps>>(
  ({ expandable: _e, ...props }) => <RuiAccordionSummary {...props} />
)(({ expandable, theme }) => ({
  padding: theme.spacing(2),
  minHeight: 0,
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
  "& .reporting-setting-accordion__expand-icon": {
    fontSize: "18px",
    transition: "transform 0.2s ease",
  },
  "&.Mui-expanded": {
    borderBottom: "1px solid",
    borderColor: theme.palette.shades.neutral[100],
    "& .reporting-setting-accordion__expand-icon": {
      transform: "rotate(-180deg)",
    },
  },
  ...(!expandable && {
    cursor: "default !important",
  }),
}));

export const AccordionDetails = styled(MuiAccordionDetails)({
  padding: 0,
});

export const AccordionDetailsContent = styled(Column)(({ theme }) => ({
  padding: theme.spacing(1, 3, 2),
}));

interface EnabledChannelsProps {
  settings: UserDistributionSetting[];
}

export const EnabledChannels = styled<React.FC<EnabledChannelsProps>>(
  ({ settings, ...props }) => {
    const enabledChannels = filter({ isEnabled: true }, settings);
    if (isEmpty(enabledChannels)) return null;

    return (
      <Row {...props}>
        <Row spacing={1} className="enabled-channels__inner-container">
          {enabledChannels.map((channel) => (
            <Img
              key={channel.method}
              src={ICON_SRC_FOR_CHANNEL[channel.method]}
              height="24px"
            />
          ))}
        </Row>
        <Row position="absolute" bottom={0} right={0}>
          <IconBackground height={12} bgcolor="shades.meadow.500">
            <CheckIcon sx={{ color: "#fff" }} />
          </IconBackground>
        </Row>
      </Row>
    );
  }
)(({ theme }) => ({
  position: "relative",
  "& .enabled-channels__inner-container": {
    border: "1px solid",
    borderColor: theme.palette.shades.neutral[100],
    borderRadius: "100px",
    padding: theme.spacing(1),
  },
}));

export const ChannelRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  width: "100%",
  "& .MuiFormControlLabel-root": {
    marginLeft: theme.spacing(-1),
  },
  "&:not(:last-of-type)": {
    borderBottom: "1px solid",
    borderColor: theme.palette.shades.neutral[100],
    paddingBottom: theme.spacing(1.5),
  },
}));

type SelectButtonProps = ButtonProps & { label: string };

export const SelectButton = styled<React.FC<SelectButtonProps>>(
  ({ disabled, label, name, startIcon, children, ...props }) => (
    <Column>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <Button
        variant="outlined"
        disabled={disabled}
        name={name}
        {...props}
        endIcon={disabled ? undefined : <ChevronDownIcon />}
      >
        <Row variant="left" spacing={1} overflow="hidden">
          {startIcon}
          <Typography variant="bodySecondaryReg" noWrap>
            {children}
          </Typography>
        </Row>
      </Button>
    </Column>
  )
)(({ theme }) => ({
  minWidth: "112px",
  maxWidth: "240px",
  height: "34px",
  borderColor: theme.palette.shades.neutral[150],
  padding: "6px 8px",
  fontWeight: 400,
  color: theme.palette.shades.neutral[800],
  justifyContent: "space-between",
  "& .MuiButton-endIcon": {
    color: theme.palette.shades.neutral[500],
    "& svg": { fontSize: "16px" },
  },

  "&.Mui-disabled": {
    backgroundColor: theme.palette.shades.neutral[50],
    color: theme.palette.shades.neutral[800],
  },
}));

export const SelectMenu = styled(Menu)({
  "& .MuiList-root": {
    paddingLeft: 0,
    paddingRight: 0,
  },
});
